/**
 * Renders a git unified-diff string with line-level coloring. git already
 * returns unified diff text from the backend (get_file_diff / commit detail),
 * so this is a self-contained renderer — no CodeMirror / extra dependency.
 */

interface DiffViewerProps {
  diff: string
  emptyHint?: string
}

function lineColor(line: string): { bg?: string; fg?: string } {
  if (line.startsWith('+++') || line.startsWith('---')) {
    return { fg: 'var(--color-text-secondary)' }
  }
  if (line.startsWith('@@')) {
    return { fg: 'var(--color-accent)', bg: 'color-mix(in srgb, var(--color-accent) 12%, transparent)' }
  }
  if (line.startsWith('+')) {
    return { fg: '#1a7f37', bg: 'color-mix(in srgb, #1a7f37 12%, transparent)' }
  }
  if (line.startsWith('-')) {
    return { fg: '#cf222e', bg: 'color-mix(in srgb, #cf222e 12%, transparent)' }
  }
  if (line.startsWith('diff ') || line.startsWith('index ')) {
    return { fg: 'var(--color-text-secondary)' }
  }
  return {}
}

export default function DiffViewer({ diff, emptyHint }: DiffViewerProps) {
  if (!diff || !diff.trim()) {
    return (
      <div style={{ padding: '1rem', color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
        {emptyHint || 'No changes to display (binary, untracked, or unchanged file).'}
      </div>
    )
  }
  const lines = diff.split('\n')
  return (
    <pre
      style={{
        margin: 0,
        padding: 0,
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: '0.8625rem',
        lineHeight: 1.5,
        overflowX: 'auto',
        background: 'var(--color-bg-secondary)',
        borderRadius: 6,
      }}
    >
      {lines.map((line, i) => {
        const c = lineColor(line)
        return (
          <div
            key={i}
            style={{
              color: c.fg,
              background: c.bg,
              padding: '0 0.75rem',
              whiteSpace: 'pre',
            }}
          >
            {line || ' '}
          </div>
        )
      })}
    </pre>
  )
}
